/**
 * CIG box note updates: legacy direct POST and queue-backed write-back path.
 * @module updateCigBoxNote
 */

import { FRONT_END_PRIORITY_VALUE_FOR_RABBITMQ_QUEUE } from "../../constants/stringLiterals";
import { RPATaskRequest } from "../../data-model/rpaTaskQueue";
import { UpdateCigBoxNoteNew } from "../../data-model/updateCigBoxNoteNew";
import { API_ENDPOINTS, RPA_PATHS_ONLY_FOR_QUEUE } from "../../enums/apiUrls";
import { PortalIdForRabbitMq } from "../../enums/portalIdsForRabbitMq";
import { getBearerToken } from "./bearerToken";
import { sendRpaRequestsToRabbitMq } from "./sendRpaRequestsToRabbitMq";

/**
 * Sends a direct POST to update CIG box data (no queue).
 *
 * @template T - Serializable request body shape.
 * @param body - JSON body for the update CIG box data endpoint.
 * @param token - Firebase bearer token for the `Authorization` header.
 * @returns Parsed JSON response from the server.
 * @throws {Error} When the HTTP response is not OK.
 */
const sendDirectApiRequest = async <T extends object>(
  body: T,
  token: string,
) => {
  const response = await fetch(API_ENDPOINTS.UPDATE_CIG_BOX_DATA, {
    method: "POST",
    body: JSON.stringify(body),
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      `Request failed with status ${response.status}: ${errorData.message || response.statusText}`,
    );
  }

  return response.json();
};

/**
 * Builds the RPA task request payload for the queue.
 */
const buildRpaTaskRequest = (
  body: UpdateCigBoxNoteNew,
  token: string,
): RPATaskRequest => ({
  payload: body,
  org_id: body.org_id,
  portal_id: PortalIdForRabbitMq.ONCOEMR,
  priority: FRONT_END_PRIORITY_VALUE_FOR_RABBITMQ_QUEUE,
  identifier: body?.order_id ?? "",
  order_id: body?.order_id ?? "",
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  },
});

/**
 * Updates a CIG box note using the legacy direct API only.
 *
 * @param body - Patient, drug, order, note text, and optional visit date.
 * @returns Parsed JSON from {@link sendDirectApiRequest}.
 * @throws {Error} When the bearer token is missing or the request fails.
 */
export const updateCigBoxNote = async (body: {
  patient_id: string;
  drug_name: string;
  note: string;
  order_id: string;
  visit_date?: string;
}) => {
  try {
    const token = await getBearerToken();
    if (!token) {
      throw new Error("Bearer token not available");
    }
    return await sendDirectApiRequest(body, token);
  } catch (error) {
    console.error("Failed to update CIG box note:", error);
    throw error;
  }
};

/**
 * Updates CIG box / write-back data via the RPA queue with direct API fallback.
 *
 * @param body - {@link UpdateCigBoxNoteNew} payload including org and order identifiers.
 * @returns Queue acknowledgement or the direct API JSON response.
 * @throws {Error} When the bearer token is missing or both queue and direct calls fail.
 */
export const updateCigBoxNoteNewEndpoint = async (
  body: UpdateCigBoxNoteNew,
) => {
  try {
    const token = await getBearerToken();
    if (!token) {
      throw new Error("Bearer token not available");
    }

    const rpaRequest = buildRpaTaskRequest(body, token);
    const queueResponse = await sendRpaRequestsToRabbitMq(
      RPA_PATHS_ONLY_FOR_QUEUE.WRITE_BACK_COMMENTS,
      rpaRequest,
    );

    if (queueResponse.success) {
      return queueResponse;
    }

    // Fallback to direct API call if queue fails
    return await sendDirectApiRequest(body, token);
  } catch (error) {
    console.error("Failed to update CIG box note:", error);
    throw error;
  }
};
